import React from 'react';

import { StatusDropdownOption } from '../StatusDropdown/StatusDropdown';

export type DataTableProps = {
    columns: DataTableColumn[]; // Fallback columns when customization is disabled
    rows: Array<object>;
    totalRowCount: number;
    emptyState: React.ReactNode;
    noSearchResults?: React.ReactNode;
    loading?: boolean;
    onRowClick?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: any) => void;
    customCellRenderers?: CellRendererMap;
    pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    openActionRowIndex?: number | null;
    valueGetterMap?: Record<string, (row: any) => React.ReactNode>;
    // Filter props
    onFiltersApply?: (filters: Record<string, any>) => void;
    filterSourceKey?: string;
    // Column customization props
    enableColumnCustomization?: boolean;
    masterColumnConfig?: DataTableColumn[]; // API-provided master config (source of truth)
    onColumnsChange?: (columns: DataTableColumn[]) => void; // Callback when derived config changes
    // Performance props
    enableVirtualization?: boolean;
    virtualizedRowHeight?: number;
    virtualizedOverscan?: number;
    disableColumnPinning?: boolean;
    maxColumnWidth?: number;
    // Horizontal display props
    horizontal?: boolean; // Display table horizontally (rows become columns)
    horizontalStyle?: React.CSSProperties;
    horizontalRowStyle?: React.CSSProperties;
    horizontalLabelStyle?: React.CSSProperties;
    horizontalValueStyle?: React.CSSProperties;
    horizontalLabelSuffix?: string;
    // Show filter prop - when true, disables filtering by show property
    disableShowFilter?: boolean;
};

export type DataTableColumn = {
    restrictBackwardMovement?: boolean;
    field: string;
    headerLabel: string;
    headerOriginalLabel: string;
    type: DataTableColumnType;
    align?: 'left' | 'center' | 'right';
    headerAlign?: 'left' | 'center' | 'right';
    headerStyle?: React.CSSProperties;
    width?: string;
    hideHeader?: boolean;
    sticky?: boolean;
    dropdownOptions?: { [key: string]: StatusDropdownOption };
    collapsedDropdownOptions?: { [key: string]: StatusDropdownOption };
    valueGetter?: (row: any) => any;
    componentName?: string; // For custom components
    label?: string;
    styles?: { [key: string]: string };
    showClearSelection?: boolean;
    filterConfig?: FilterConfig;
    unit?: string;
    unitLeft?: boolean;
    secondaryUnit?: string;
    allowDecimal?: boolean; // If false, prevents decimal input in NumberCellRenderer (default: true)
    disable?: boolean;
    dateFormat?: string;
    show?: boolean;
};

export type NullValueOption = {
    label?: string; // Custom label for the null value option (default: "Not Set")
    position?: 'top' | 'bottom'; // Where to display the option (default: "top")
};

export type FilterConfig = {
    enabled: boolean;
    filterType: FilterType;
    label?: string;
    excludedValues?: string[];
    options?: { value: string | null; label: string }[]; // Static options for the filter
    nullValueOption?: NullValueOption; // Configuration for null value option display
};

export enum FilterType {
    MULTI_SELECT = 'MULTI_SELECT',
    RANGE = 'RANGE',
    DATE_RANGE = 'DATE_RANGE',
}

export enum DataTableColumnType {
    DATE_FORMAT = 'DATE_FORMAT',
    TEXT = 'TEXT',
    ACTION = 'ACTION',
    OWNER = 'OWNER',
    COMMENT = 'COMMENT',
    SINGLE_SELECT_DROPDOWN = 'SINGLE_SELECT_DROPDOWN',
    MULTI_SELECT_DROPDOWN = 'MULTI_SELECT_DROPDOWN',
    GRADIENT_TEXT = 'GRADIENT_TEXT',
    MODAL_ACTION = 'MODAL_ACTION',
    EXPANDABLE = 'EXPANDABLE',
    NUMBER = 'NUMBER',
    DATE_PICKER = 'DATE_PICKER',
    PILL = 'PILL',

    // Custom Lead Table Types
    COMPANY_NAME_CTA = 'COMPANY_NAME_CTA',

    //ComponentCell
    COMPONENT = 'COMPONENT',

    // Status Timeline feature
    LAST_STATUS_UPDATE = 'LAST_STATUS_UPDATE',
    ADD_COMMENT = 'ADD_COMMENT',
}

// Column types that should be excluded from customization
export const EXCLUDED_COLUMN_TYPES = [
    DataTableColumnType.ACTION,
    DataTableColumnType.MODAL_ACTION,
    DataTableColumnType.EXPANDABLE,
    DataTableColumnType.COMPONENT,
];

export type CellRendererProps = {
    value: any;
    row: any;
    column: DataTableColumn;
    rowIndex: number;
    columnIndex: number;
    dynamicProps?: Record<string, any>;
};

export type CellRenderer = React.ComponentType<CellRendererProps>;
export type CellRendererMap = Partial<Record<DataTableColumnType, CellRenderer>>;

// Filter renderer types
export type FilterRendererProps = {
    column: DataTableColumn;
    value: any;
    onChange: (value: any) => void;
    options?: { label: string; value: string | null }[];
};

export type FilterRenderer = React.ComponentType<FilterRendererProps>;
export type FilterRendererMap = Partial<Record<FilterType, FilterRenderer>>;

export interface ColumnMetadata {
    column: DataTableColumn;
    originalIndex: number;
    pinnedInfo: { field: string; position: 'left' | 'right' } | undefined;
    pinnedIndex: number;
    pinnedClass: string;
    style: React.CSSProperties;
    headerStyle: React.CSSProperties;
}
export interface ModalActionCellData {
    statusLabel: string;
    hoverBgColor: string;
    textColor: string;
    iconFill: string;
    hoverFill: string;
    hoverTextColor: string;
    bgColor?: string;
    icon?: React.ComponentType<any>;
}

export interface BorrowerRequirementsCount {
    pending: number;
    total: number;
    complete: number;
}

export interface ModalActionConfig {
    icon: React.ComponentType<any>;
    handler: (row: any, field?: string) => void;
    computeData?: (row: any) => ModalActionCellData;
}

export interface ModalActionFieldConfig {
    [fieldName: string]: ModalActionConfig;
}
